import { useQuery } from '@tanstack/react-query';
import { AlertOctagon, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { uptimeText, uptimeTone, type MonitoringOverview } from '../lib/monitoring';
import { Metric } from './ui';

/**
 * Monitoring's overview, read every 30 seconds — the Overview page's and the
 * dashboard's, from one cache entry. `enabled` false for whoever may not see
 * Monitoring (`monitoring.manage`): the API would refuse them anyway.
 */
export function useMonitoringOverview(enabled = true) {
  return useQuery({
    queryKey: ['monitoring', 'overview'],
    queryFn: () => api.get<MonitoringOverview>('/monitoring/overview'),
    refetchInterval: 30_000,
    enabled,
  });
}

/**
 * Monitoring's six figures: servers online, URLs up, what is down now, uptime
 * over a day and a month, certificates running out. On the Overview page and,
 * for the administrators, on the dashboard above Company (operator, 2026-10-20:
 * "add this section on dashboard above company"). The first three open the page
 * behind them.
 */
export function MonitoringTiles({ data, className }: { data: MonitoringOverview; className?: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const downNow = data.openIncidents.length;
  const expiring = data.certificates.filter((c) => c.daysLeft <= c.warnDays);
  const tone = (percent: number | null) => {
    const value = uptimeTone(percent);
    return value === 'info' || value === 'plasma' || value === 'brand' ? 'neutral' : value;
  };

  return (
    // Six narrow tiles: "Certificates expiring" takes two lines, so every label keeps room for two.
    <div className={clsx('grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6', className)}>
      <Metric
        label={t('monitoring.overview.servers')}
        labelLines={2}
        value={`${data.servers.ONLINE}/${data.servers.total}`}
        hint={t('monitoring.overview.serversHint', { warning: data.servers.WARNING, offline: data.servers.OFFLINE })}
        tone={data.servers.OFFLINE > 0 ? 'error' : data.servers.WARNING > 0 ? 'warning' : 'neutral'}
        onClick={() => navigate('/monitoring/servers')}
      />
      <Metric
        label={t('monitoring.overview.urls')}
        labelLines={2}
        value={`${data.urls.up + data.urls.degraded}/${data.urls.total - data.urls.paused}`}
        hint={t('monitoring.overview.urlsHint', { degraded: data.urls.degraded, paused: data.urls.paused })}
        onClick={() => navigate('/monitoring/urls')}
      />
      <Metric
        label={t('monitoring.overview.downNow')}
        labelLines={2}
        value={formatNumber(downNow, locale)}
        hint={downNow > 0 ? t('monitoring.overview.downNowHint') : t('monitoring.overview.allGood')}
        tone={downNow > 0 ? 'error' : 'success'}
        icon={<AlertOctagon className="h-4 w-4" />}
        onClick={() => navigate('/monitoring/incidents?open=true')}
      />
      <Metric label={t('monitoring.url.uptimeDay')} labelLines={2} value={uptimeText(data.uptime.day)} tone={tone(data.uptime.day)} />
      <Metric label={t('monitoring.url.uptimeMonth')} labelLines={2} value={uptimeText(data.uptime.month)} tone={tone(data.uptime.month)} />
      <Metric
        label={t('monitoring.overview.certificates')}
        labelLines={2}
        value={formatNumber(expiring.length, locale)}
        hint={t('monitoring.overview.certificatesHint')}
        tone={expiring.length > 0 ? 'warning' : 'neutral'}
        icon={<ShieldCheck className="h-4 w-4" />}
      />
    </div>
  );
}
